// Iter 104 — Extracted from SwipeCards.js verbatim.
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { hapticTap } from "../../utils/haptics";
import QuickTradeModal from "../QuickTradeModal";
import { f$, fN, fP, miniChartOpts, fullChartOpts, API, _coinGradient } from "./_shared";
import { Skel } from "./Skel";

export function CryptoExpanded() {
  // iter 94 — localStorage SWR: hydrate from last-known payload for instant
  // render, then refresh in background. Drops the perceived "blank screen"
  // on tile-open to zero when data is recent.
  const [universe, setUniverse] = useState(() => {
    try {
      const cached = localStorage.getItem("zi_crypto_universe_v1");
      if (cached) {
        const { payload, ts } = JSON.parse(cached);
        // Accept anything < 10 minutes old for instant-render. Fresh refresh
        // fires on mount regardless.
        if (Date.now() - ts < 600000) return payload;
      }
    } catch {}
    return null;
  });
  const [sym, setSym] = useState(() => {
    // iter 97 — "Tap from LIVE POSITIONS" hint. If the user tapped a crypto
    // holding on the home grid, jump straight to that coin's detail view.
    try {
      const hint = localStorage.getItem("zi_crypto_open_sym");
      if (hint) {
        localStorage.removeItem("zi_crypto_open_sym");
        return hint.toUpperCase();
      }
    } catch {}
    return null;
  });           // null = universe view, else coin detail
  const [detail, setDetail] = useState(null);     // {bars, insight, quote}
  const [period, setPeriod] = useState("1d");     // 1d | 5d | 1mo | 3mo | 6mo | 1y
  const [loadingU, setLoadingU] = useState(() => {
    try {
      const cached = localStorage.getItem("zi_crypto_universe_v1");
      if (cached) return false;
    } catch {}
    return true;
  });
  const [loadingD, setLoadingD] = useState(false);
  // iter 95 — inline QuickTrade for crypto buy/sell
  const [quickTrade, setQuickTrade] = useState(null); // {position, mode}

  const loadUniverse = async () => {
    try {
      const r = await axios.get(`${API}/crypto/universe?limit=12`);
      setUniverse(r.data);
      try { localStorage.setItem("zi_crypto_universe_v1", JSON.stringify({ payload: r.data, ts: Date.now() })); } catch {}
    } catch { if (!universe) setUniverse({ coins: [] }); }
    setLoadingU(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadUniverse(); const iv = setInterval(loadUniverse, 30000); return () => clearInterval(iv); }, []);

  const loadDetail = async (s, p = period) => {
    setLoadingD(true);
    try {
      const [histR, insR] = await Promise.all([
        axios.get(`${API}/crypto/history/${s}?period=${p}&interval=${p === "1d" ? "1h" : "1d"}`),
        axios.get(`${API}/crypto/insight/${s}`),
      ]);
      setDetail({ bars: histR.data?.bars || [], insight: insR.data || null, symbol: s });
    } catch {
      setDetail({ bars: [], insight: null, symbol: s, error: true });
    }
    setLoadingD(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (sym) loadDetail(sym, period); }, [sym, period]);

  const verdictColor = (v) => v === "elite" ? "#fbbf24" : v === "strong" ? "#4ade80" : v === "moderate" ? "#60a5fa" : "#8b949e";
  const signColor = (n) => (n || 0) >= 0 ? "#4ade80" : "#f87171";

  // ── Sparkline renderer (24-point mini line) ─────────────────────────────
  const Sparkline = ({ data, width = 96, height = 32, stroke = "#4ade80" }) => {
    if (!data || data.length < 2) return <div style={{ width, height }} />;
    const mn = Math.min(...data), mx = Math.max(...data), r = Math.max(0.0001, mx - mn);
    const pts = data.map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - mn) / r) * height;
      return `${x},${y}`;
    }).join(" ");
    return (
      <svg width={width} height={height}>
        <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  // Helper: open QuickTradeModal for a given coin (mode = "buy_more" or "sell")
  const openCryptoTrade = (coinSym, price, mode = "buy_more", heldQty = 0) => {
    hapticTap();
    setQuickTrade({
      position: {
        symbol: coinSym,        // "BTC" — bare, backend normalizes
        ticker: coinSym,
        quantity: heldQty,      // 0 for new-position buys; real qty drives max-sell
        lastPrice: price,
        avgCost: price,
        type: "CRYPTO",
      },
      mode,
    });
  };

  // ── Detail view ─────────────────────────────────────────────────────────
  if (sym) {
    const d = detail;
    const ins = d?.insight || {};
    const bars = d?.bars || [];
    const chartClose = bars.map(b => b?.c).filter(v => v != null);
    const chartData = chartClose.length > 1 ? {
      labels: bars.map(b => new Date(b.t).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
      datasets: [{
        data: chartClose,
        borderColor: chartClose[chartClose.length - 1] >= chartClose[0] ? "#4ade80" : "#f87171",
        backgroundColor: chartClose[chartClose.length - 1] >= chartClose[0] ? "rgba(74,222,128,0.10)" : "rgba(248,113,113,0.10)",
        fill: true, borderWidth: 1.8, pointRadius: 0, tension: 0.2,
      }],
    } : null;
    const vColor = ins.verdict === "bullish" ? "#4ade80" : ins.verdict === "bearish" ? "#f87171" : "#60a5fa";
    const [g1, g2] = _coinGradient(sym);

    return (
      <div style={{ padding: "0 16px 110px" /* bottom pad for sticky CTAs */ }} data-testid="crypto-detail">
        <button onClick={() => setSym(null)} style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 14 }}>&larr; Back to Universe</button>

        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: `linear-gradient(135deg, ${g1}, ${g2})`,
            display: "grid", placeItems: "center",
            fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: 0.5,
            boxShadow: `0 10px 30px ${g1}30`,
            flexShrink: 0,
          }}>{sym.slice(0, 4)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#e6edf3", letterSpacing: -0.4 }}>{sym}</div>
            <div style={{ fontSize: 11, color: "#8b949e", letterSpacing: 0.3 }}>Live · Public.com · {sym}-USD</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3" }}>${Number(ins.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div style={{ fontSize: 12, color: signColor(ins.change30dPct), fontWeight: 700 }}>
              {(ins.change30dPct || 0) >= 0 ? "+" : ""}{(ins.change30dPct || 0).toFixed(2)}% · 30d
            </div>
          </div>
        </div>

        {/* Period chips */}
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {["1d", "5d", "1mo", "3mo", "6mo", "1y"].map(p => (
            <button
              key={p}
              data-testid={`crypto-period-${p}`}
              onClick={() => { hapticTap(); setPeriod(p); }}
              style={{
                flex: 1, padding: "5px 4px", fontSize: 10, fontWeight: 700, borderRadius: 8,
                border: "1px solid", borderColor: period === p ? "#67e8f9" : "rgba(255,255,255,0.08)",
                background: period === p ? "rgba(103,232,249,0.12)" : "rgba(255,255,255,0.03)",
                color: period === p ? "#67e8f9" : "#8b949e", cursor: "pointer", WebkitTapHighlightColor: "transparent",
              }}
            >{p}</button>
          ))}
        </div>

        {/* Price chart */}
        <div style={{ height: 170, padding: 10, borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 14 }}>
          {loadingD && <div style={{ padding: 30, textAlign: "center", color: "#636e7b", fontSize: 11 }}>Loading chart…</div>}
          {!loadingD && chartData && (
            <Line data={chartData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(13,17,23,0.95)", titleFont: { size: 10 }, bodyFont: { size: 10 } } },
              scales: {
                x: { ticks: { color: "#636e7b", font: { size: 8 }, maxTicksLimit: 6 }, grid: { display: false } },
                y: { ticks: { color: "#636e7b", font: { size: 8 }, callback: v => `$${v.toLocaleString()}` }, grid: { color: "rgba(255,255,255,0.03)" } },
              },
            }} />
          )}
          {!loadingD && !chartData && <div style={{ padding: 30, textAlign: "center", color: "#636e7b", fontSize: 11 }}>No chart data</div>}
        </div>

        {/* Stats grid — Support / Resistance / Vol / Risk */}
        {ins?.supportResistance && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
            <div style={{ padding: 10, borderRadius: 10, background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.14)" }}>
              <div style={{ fontSize: 9, color: "#4ade80", letterSpacing: 1.2, textTransform: "uppercase" }}>Support</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", marginTop: 2 }}>${(ins.supportResistance.support || 0).toLocaleString()}</div>
            </div>
            <div style={{ padding: 10, borderRadius: 10, background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.14)" }}>
              <div style={{ fontSize: 9, color: "#f87171", letterSpacing: 1.2, textTransform: "uppercase" }}>Resistance</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", marginTop: 2 }}>${(ins.supportResistance.resistance || 0).toLocaleString()}</div>
            </div>
            <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ fontSize: 9, color: "#8b949e", letterSpacing: 1.2, textTransform: "uppercase" }}>30d Vol</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e6edf3", marginTop: 2 }}>{ins.vol30dPct || 0}%</div>
            </div>
            <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ fontSize: 9, color: "#8b949e", letterSpacing: 1.2, textTransform: "uppercase" }}>Risk</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: ins.riskScore >= 70 ? "#f87171" : ins.riskScore >= 40 ? "#fbbf24" : "#4ade80", marginTop: 2 }}>{ins.riskScore || 0}</div>
            </div>
          </div>
        )}

        {/* 7-day forecast band */}
        {ins?.forecast7d && (
          <div style={{ padding: 12, borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#8b949e", letterSpacing: 1.5, marginBottom: 6 }}>7-DAY FORECAST · 90% CONFIDENCE BAND</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <div><span style={{ color: "#f87171" }}>p5</span> <strong style={{ color: "#e6edf3" }}>${(ins.forecast7d.p5 || 0).toLocaleString()}</strong></div>
              <div><span style={{ color: "#60a5fa" }}>p50</span> <strong style={{ color: "#e6edf3" }}>${(ins.forecast7d.p50 || 0).toLocaleString()}</strong></div>
              <div><span style={{ color: "#4ade80" }}>p95</span> <strong style={{ color: "#e6edf3" }}>${(ins.forecast7d.p95 || 0).toLocaleString()}</strong></div>
            </div>
          </div>
        )}

        {/* Omega ZI Deep-Dive */}
        {ins?.summary && (
          <div data-testid="crypto-deep-dive" style={{ padding: 14, borderRadius: 14, background: "linear-gradient(135deg, rgba(103,232,249,0.10), rgba(167,139,250,0.04))", border: "1px solid rgba(103,232,249,0.22)", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#67e8f9", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#67e8f9", letterSpacing: 1.5 }}>OMEGA ZI AI · CRYPTO DEEP-DIVE</span>
              {ins.verdict && (
                <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: `${vColor}22`, color: vColor, letterSpacing: 1 }}>
                  {String(ins.verdict).toUpperCase()}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#e6edf3", lineHeight: 1.6, marginBottom: 10 }}>{ins.summary}</div>
            {ins.recommendation && (
              <div style={{ padding: 10, borderRadius: 10, background: "rgba(0,0,0,0.25)", borderLeft: `3px solid ${vColor}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: vColor, letterSpacing: 1, marginBottom: 3 }}>RECOMMENDATION</div>
                <div style={{ fontSize: 12, color: "#c9d1d9", lineHeight: 1.5 }}>{ins.recommendation}</div>
              </div>
            )}
          </div>
        )}

        {/* Sticky bottom Buy/Sell bar */}
        <div
          data-testid="crypto-trade-bar"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 90,
            padding: "12px 14px 18px",
            background: "linear-gradient(to top, rgba(6,10,18,0.98) 0%, rgba(6,10,18,0.88) 70%, rgba(6,10,18,0) 100%)",
            display: "flex", gap: 10, justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div style={{ display: "flex", gap: 10, width: "100%", maxWidth: 480, pointerEvents: "auto" }}>
            <button
              data-testid={`crypto-sell-${sym}`}
              onClick={() => openCryptoTrade(sym, ins.price || 0, "sell", 0)}
              style={{
                flex: 1, height: 52, borderRadius: 26, border: "1px solid rgba(248,113,113,0.45)",
                background: "rgba(248,113,113,0.12)", color: "#fca5a5",
                fontSize: 14, fontWeight: 800, letterSpacing: 1.2, cursor: "pointer",
                textTransform: "uppercase", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
              }}
            >Sell</button>
            <button
              data-testid={`crypto-buy-${sym}`}
              onClick={() => openCryptoTrade(sym, ins.price || 0, "buy_more", 0)}
              style={{
                flex: 1, height: 52, borderRadius: 26, border: "none",
                background: `linear-gradient(135deg, ${g1}, ${g2})`, color: "#fff",
                fontSize: 14, fontWeight: 800, letterSpacing: 1.2, cursor: "pointer",
                textTransform: "uppercase",
                boxShadow: `0 10px 24px ${g1}55`,
              }}
            >Buy {sym}</button>
          </div>
        </div>

        {quickTrade && (
          <QuickTradeModal
            position={quickTrade.position}
            mode={quickTrade.mode}
            onClose={() => setQuickTrade(null)}
            onSuccess={() => setQuickTrade(null)}
          />
        )}
      </div>
    );
  }

  // ── Universe list view (modernized iter 95) ──────────────────────────────
  const coins = universe?.coins || [];
  // Market-pulse: top gainer + top loser + elite-count from current list.
  const gainers = coins.filter(c => (c.change24hPct || 0) > 0).sort((a, b) => b.change24hPct - a.change24hPct);
  const losers = coins.filter(c => (c.change24hPct || 0) < 0).sort((a, b) => a.change24hPct - b.change24hPct);
  const eliteCount = coins.filter(c => c.verdict === "elite").length;

  return (
    <div style={{ padding: "0 16px 32px" }} data-testid="crypto-universe">
      {/* Asymmetric header — left-aligned instead of centered. Feels less AI-slop. */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, background: "rgba(103,232,249,0.10)", border: "1px solid rgba(103,232,249,0.25)", marginBottom: 10 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#67e8f9", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: "#67e8f9", letterSpacing: 2 }}>LIVE · PUBLIC.COM</span>
        </div>
        <div style={{ fontSize: 30, fontWeight: 800, color: "#e6edf3", letterSpacing: -1, lineHeight: 1 }}>Crypto Universe</div>
        <div style={{ fontSize: 12, color: "#8b949e", marginTop: 4 }}>Omega Zi crypto score · momentum × volume × spread</div>
      </div>

      {/* Market pulse pills — top mover / top loser / elite count */}
      {!loadingU && coins.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }} data-testid="crypto-pulse">
          {gainers[0] && (
            <button
              onClick={() => { hapticTap(); setSym(gainers[0].symbol); }}
              style={{
                padding: "10px 12px", borderRadius: 14, border: "1px solid rgba(74,222,128,0.22)",
                background: "linear-gradient(135deg, rgba(74,222,128,0.14), rgba(74,222,128,0.04))",
                textAlign: "left", cursor: "pointer", WebkitTapHighlightColor: "transparent",
              }}
            >
              <div style={{ fontSize: 9, color: "#4ade80", letterSpacing: 1.5, fontWeight: 700 }}>TOP MOVER</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>{gainers[0].symbol}</div>
              <div style={{ fontSize: 11, color: "#4ade80", fontWeight: 700, marginTop: 1 }}>+{gainers[0].change24hPct.toFixed(2)}%</div>
            </button>
          )}
          {losers[0] && (
            <button
              onClick={() => { hapticTap(); setSym(losers[0].symbol); }}
              style={{
                padding: "10px 12px", borderRadius: 14, border: "1px solid rgba(248,113,113,0.22)",
                background: "linear-gradient(135deg, rgba(248,113,113,0.14), rgba(248,113,113,0.04))",
                textAlign: "left", cursor: "pointer", WebkitTapHighlightColor: "transparent",
              }}
            >
              <div style={{ fontSize: 9, color: "#f87171", letterSpacing: 1.5, fontWeight: 700 }}>TOP LOSER</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#e6edf3", marginTop: 2 }}>{losers[0].symbol}</div>
              <div style={{ fontSize: 11, color: "#f87171", fontWeight: 700, marginTop: 1 }}>{losers[0].change24hPct.toFixed(2)}%</div>
            </button>
          )}
          <div
            style={{
              padding: "10px 12px", borderRadius: 14, border: "1px solid rgba(251,191,36,0.22)",
              background: "linear-gradient(135deg, rgba(251,191,36,0.14), rgba(251,191,36,0.04))",
            }}
          >
            <div style={{ fontSize: 9, color: "#fbbf24", letterSpacing: 1.5, fontWeight: 700 }}>ELITE SETUPS</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e6edf3", marginTop: 2, lineHeight: 1.05 }}>{eliteCount}</div>
            <div style={{ fontSize: 10, color: "#8b949e", marginTop: 1 }}>Zi ≥ 72</div>
          </div>
        </div>
      )}

      {loadingU && (<><Skel w="100%" h={72} mb={10} radius={12} /><Skel w="100%" h={72} mb={10} radius={12} /><Skel w="100%" h={72} mb={0} radius={12} /></>)}
      {!loadingU && coins.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#8b949e", fontSize: 12 }}>Crypto feed warming up…</div>}

      {!loadingU && coins.map(c => {
        const sg = signColor(c.change24hPct);
        const [g1, g2] = _coinGradient(c.symbol);
        const vc = verdictColor(c.verdict);
        const isElite = c.verdict === "elite";
        return (
          <div
            key={c.symbol}
            data-testid={`crypto-row-${c.symbol}`}
            style={{
              position: "relative", marginBottom: 10,
              borderRadius: 16,
              background: isElite
                ? "linear-gradient(135deg, rgba(251,191,36,0.08), rgba(255,255,255,0.02))"
                : "rgba(255,255,255,0.02)",
              border: `1px solid ${isElite ? "rgba(251,191,36,0.22)" : "rgba(255,255,255,0.05)"}`,
              overflow: "hidden",
            }}
          >
            {/* Main row: tap → detail */}
            <button
              onClick={() => { hapticTap(); setSym(c.symbol); }}
              style={{
                width: "100%", padding: "14px 14px 12px",
                background: "transparent", border: "none",
                display: "flex", alignItems: "center", gap: 14, textAlign: "left",
                cursor: "pointer", WebkitTapHighlightColor: "transparent",
              }}
            >
              {/* Coin-specific gradient avatar */}
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: `linear-gradient(135deg, ${g1}, ${g2})`,
                display: "grid", placeItems: "center",
                fontSize: c.symbol.length > 3 ? 9 : 11, fontWeight: 800, color: "#fff",
                flexShrink: 0,
                boxShadow: `0 6px 16px ${g1}35`,
              }}>{c.symbol}</div>

              {/* Left column — ticker + change */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: "#e6edf3", letterSpacing: -0.2 }}>{c.symbol}</span>
                  {isElite && (
                    <span style={{ fontSize: 9, fontWeight: 800, color: "#fbbf24", letterSpacing: 1, textTransform: "uppercase", padding: "1px 6px", borderRadius: 4, background: "rgba(251,191,36,0.14)" }}>Elite</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: sg }}>
                    {c.change24hPct >= 0 ? "▲" : "▼"} {Math.abs(c.change24hPct).toFixed(2)}%
                  </span>
                  <span style={{ fontSize: 10, color: "#636e7b" }}>24h</span>
                </div>
              </div>

              {/* Right column — price + sparkline stacked */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#e6edf3", fontVariantNumeric: "tabular-nums" }}>
                  ${c.price.toLocaleString(undefined, { minimumFractionDigits: c.price < 1 ? 4 : 2, maximumFractionDigits: c.price < 1 ? 4 : 2 })}
                </span>
                <Sparkline data={c.sparkline || []} stroke={sg} width={84} height={28} />
              </div>
            </button>

            {/* Footer row: Zi bar + inline Buy CTA */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 14px 12px" }}>
              {/* Zi progress bar */}
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 9, color: "#636e7b", letterSpacing: 1.5, fontWeight: 700, minWidth: 14 }}>Zi</span>
                <div style={{ flex: 1, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                  <div style={{
                    width: `${Math.max(3, Math.min(100, c.zi))}%`,
                    height: "100%",
                    background: vc,
                    boxShadow: `0 0 8px ${vc}55`,
                    transition: "width 420ms ease",
                  }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 800, color: vc, fontVariantNumeric: "tabular-nums", minWidth: 24, textAlign: "right" }}>{c.zi.toFixed(0)}</span>
              </div>
              {/* Quick-buy CTA */}
              <button
                data-testid={`crypto-quickbuy-${c.symbol}`}
                onClick={(e) => { e.stopPropagation(); openCryptoTrade(c.symbol, c.price, "buy_more", 0); }}
                style={{
                  padding: "6px 14px", borderRadius: 999,
                  border: "none",
                  background: `linear-gradient(135deg, ${g1}, ${g2})`,
                  color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: 1.2,
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                  boxShadow: `0 4px 12px ${g1}40`,
                }}
              >BUY</button>
            </div>
          </div>
        );
      })}

      {!loadingU && universe?.updated && (
        <div style={{ fontSize: 10, color: "#444c56", textAlign: "center", marginTop: 6 }}>
          Updated {new Date(universe.updated).toLocaleTimeString()}
        </div>
      )}

      {quickTrade && (
        <QuickTradeModal
          position={quickTrade.position}
          mode={quickTrade.mode}
          onClose={() => setQuickTrade(null)}
          onSuccess={() => setQuickTrade(null)}
        />
      )}
    </div>
  );
}
